import {createAction, handleActions} from 'redux-actions';
import {call, put, takeLatest} from 'redux-saga/effects';

import {finishLoading, startLoading} from '../loading';
import createRequestSaga, {createRequestActionTypes} from '../../api/createRequestSaga';
import * as docsAPI from '../../api/admin/docs';
import {SERVER_URL} from '../../api/url';

// 액션 타입
const INITIALIZE = 'docs/INITIALIZE';

const INITIALIZE_MODAL = 'docs/INITIALIZE_MODAL';

const CHANGE_FIELD = 'docs/CHANGE_FIELD';

const TOGGLE_CATEGORY = 'docs/TOGGLE_CATEGORY'; // 드롭다운 아이콘 클릭 시, 열림/닫힘 기능을 구현하기 위한 액션 타입

const OPEN_CATEGORY = 'docs/OPEN_CATEGORY'; // 카테고리 이름 클릭 시, 열리기만 하는 기능을 구현하기 위한 액션 타입

const [READ_CATEGORY_LIST, READ_CATEGORY_LIST_SUCCESS, READ_CATEGORY_LIST_FAILURE] = createRequestActionTypes('docs/READ_CATEGORY_LIST');

const [READ_DOCS_LIST, READ_DOCS_LIST_SUCCESS, READ_DOCS_LIST_FAILURE] = createRequestActionTypes('docs/READ_DOCS_LIST');

const [DOWNLOAD_DOCS_LIST, DOWNLOAD_DOCS_LIST_SUCCESS, DOWNLOAD_DOCS_LIST_FAILURE] = createRequestActionTypes('docs/DOWNLOAD_DOCS_LIST');

const [CHECK_DUPLICATED_NAME, CHECK_DUPLICATED_NAME_SUCCESS, CHECK_DUPLICATED_NAME_FAILURE] = createRequestActionTypes('docs/CHECK_DUPLICATED_NAME');

const [UPLOAD_DOCS, UPLOAD_DOCS_SUCCESS, UPLOAD_DOCS_FAILURE] = createRequestActionTypes('docs/UPLOAD_DOCS');

const [CREATE_CATEGORY, CREATE_CATEGORY_SUCCESS, CREATE_CATEGORY_FAILURE] = createRequestActionTypes('docs/CREATE_CATEGORY');

const [UPDATE_CATEGORY, UPDATE_CATEGORY_SUCCESS, UPDATE_CATEGORY_FAILURE] = createRequestActionTypes('docs/UPDATE_CATEGORY');

const [UPDATE_DOCS, UPDATE_DOCS_SUCCESS, UPDATE_DOCS_FAILURE] = createRequestActionTypes('docs/UPDATE_DOCS');

const [DELETE_DOCS_LIST, DELETE_DOCS_LIST_SUCCESS, DELETE_DOCS_LIST_FAILURE] = createRequestActionTypes('docs/DELETE_DOCS_LIST');

// 액션 생성 함수
export const initialize = createAction(INITIALIZE);

export const initializeModal = createAction(INITIALIZE_MODAL);

export const changeField = createAction(
    CHANGE_FIELD,
    ({ name, value }) => ({ name, value })
);

export const toggleCategory = createAction(
    TOGGLE_CATEGORY,
    (categoryId) => (categoryId)
);

export const openCategory = createAction(
    OPEN_CATEGORY,
    (categoryId) => (categoryId)
);

export const readCategoryList = createAction(READ_CATEGORY_LIST);

export const readDocsList = createAction(
    READ_DOCS_LIST,
    ({ id, name, page }) => ({ id, name, page })
);

export const downloadDocsList = createAction(
    DOWNLOAD_DOCS_LIST,
    (docsList) => (docsList)
);

export const checkDuplicatedName = createAction(
    CHECK_DUPLICATED_NAME,
    (nameList) => (nameList)
);

export const uploadDocs = createAction(
    UPLOAD_DOCS,
    (formData) => (formData)
);

export const createCategory = createAction(
    CREATE_CATEGORY,
    ({ name, description, currentId }) => ({ name, description, currentId })
);

export const updateCategory = createAction(
    UPDATE_CATEGORY,
    ({ id, name, description }) => ({ id, name, description })
);

export const updateDocs = createAction(
    UPDATE_DOCS,
    ({ id, name, docsType }) => ({ id, name, docsType })
);

export const deleteDocsList = createAction(
    DELETE_DOCS_LIST,
    ({ idList }) => ({ idList })
);

// 사가
const readCategoryListSaga = createRequestSaga(READ_CATEGORY_LIST, docsAPI.readCategoryList);

const readDocsListSaga = createRequestSaga(READ_DOCS_LIST, docsAPI.readDocsList);

function* downloadDocsListSaga(action) {
    yield put(startLoading(DOWNLOAD_DOCS_LIST));
    
    try {
        const response = yield call(docsAPI.downloadDocsList, action.payload);
        
        yield put({
            type: DOWNLOAD_DOCS_LIST_SUCCESS,
            payload: response.data
        });

        const { downloadLink } = response.data.data;
        
        if (downloadLink && downloadLink.length > 0) {
            for (const link of downloadLink) {
                const url = `${SERVER_URL}${link}`; // 정의된 FILE_DOWNLOAD_URL과 함께 완전한 URL 생성

                let fileName = link.split('/'); // 파일명 추출
                fileName = fileName[fileName.length - 1];
                
                if (fileName.toLowerCase().endsWith('.pdf')) { // PDF 파일은 새 탭에서 열기
                    window.open(url, '_blank');
                } else { // 기타 파일은 현재 탭에서 다운로드
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = fileName;
                    a.setAttribute('download', fileName);
                    
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                }
            }
        }
    } catch (e) {
        yield put({
            type: DOWNLOAD_DOCS_LIST_FAILURE,
            payload: e,
            error: true
        });
    }
    
    yield put(finishLoading(DOWNLOAD_DOCS_LIST));
}

const checkDuplicatedNameSaga = createRequestSaga(CHECK_DUPLICATED_NAME, docsAPI.checkDuplicatedName);

const uploadDocsSaga = createRequestSaga(UPLOAD_DOCS, docsAPI.uploadDocs);

const createCategorySaga = createRequestSaga(CREATE_CATEGORY, docsAPI.createCategory);

const updateCategorySaga = createRequestSaga(UPDATE_CATEGORY, docsAPI.updateCategory);

const updateDocsSaga = createRequestSaga(UPDATE_DOCS, docsAPI.updateDocs);

const deleteDocsListSaga = createRequestSaga(DELETE_DOCS_LIST, docsAPI.deleteDocsList);

export function* docsSaga() {
    yield takeLatest(READ_CATEGORY_LIST, readCategoryListSaga);
    yield takeLatest(READ_DOCS_LIST, readDocsListSaga);
    yield takeLatest(DOWNLOAD_DOCS_LIST, downloadDocsListSaga);
    yield takeLatest(CHECK_DUPLICATED_NAME, checkDuplicatedNameSaga);
    yield takeLatest(UPLOAD_DOCS, uploadDocsSaga);
    yield takeLatest(CREATE_CATEGORY, createCategorySaga);
    yield takeLatest(UPDATE_CATEGORY, updateCategorySaga);
    yield takeLatest(UPDATE_DOCS, updateDocsSaga);
    yield takeLatest(DELETE_DOCS_LIST, deleteDocsListSaga);
};

const findMainCategoryId = (categoryList, subCategoryId) => { // 헬퍼 함수 : 서브 카테고리의 메인 카테고리 id 찾기
    for (const mainCategory of categoryList) {
        if (mainCategory.categoryList && mainCategory.categoryList.some(subCategory => subCategory.id === subCategoryId)) {
            return mainCategory.id;
        }
    }
    
    return null;
};

// 상태
const initialState = {
    currentId: 0,               // 현재 카테고리 id. 모든 카테고리는 0
    categoryName: '모든 카테고리',
    categoryDescription: '',
    depth: 0,                   // 모든 카테고리는 0
    openCategoryList: [],       // 현재 열려있는 카테고리 id 목록
    name: null,                 // 필터링에 사용되는 이름
    page: 1,
    pagination: null,
    readCategoryListResponse: null,
    readCategoryListError: null,
    readDocsListResponse: null,
    readDocsListError: null,
    downloadResponse: null,
    downloadError: null,
    duplicateResponse: null,
    duplicateError: null,
    uploadResponse: null,
    uploadError: null,
    createResponse: null,
    createError: null,
    updateCategoryResponse: null,
    updateCategoryError: null,
    updateDocsResponse: null,
    updateDocsError: null,
    deleteResponse: null,
    deleteError: null
};

// 리듀서
const docs = handleActions(
    {
        [INITIALIZE]: () => (initialState),
        [INITIALIZE_MODAL]: (state) => ({
            ...state,
            duplicateResponse: null,
            duplicateError: null,
            uploadResponse: null,
            uploadError: null,
            createResponse: null,
            createError: null,
            updateCategoryResponse: null,
            updateCategoryError: null,
            updateDocsResponse: null,
            updateDocsError: null,
            deleteResponse: null,
            deleteError: null
        }),
        [CHANGE_FIELD]: (state, { payload: { name, value } }) => ({
            ...state,
            [name]: value
        }),
        [TOGGLE_CATEGORY]: (state, { payload: categoryId }) => {
            const isOpen = state.openCategoryList.includes(categoryId);

            const updatedOpenCategoryList = isOpen
                ? state.openCategoryList.filter(id => id !== categoryId)
                : [...state.openCategoryList, categoryId];
            
            return {
                ...state,
                openCategoryList: updatedOpenCategoryList
            };
        },
        [OPEN_CATEGORY]: (state, { payload: categoryId }) => {
            if (state.openCategoryList.includes(categoryId)) {      // 이미 열려있는 메인 카테고리인 경우
                return state;
            }
            
            if (state.readCategoryListResponse) {
                const mainId = findMainCategoryId(state.readCategoryListResponse, categoryId);
                
                if (mainId) {                                       // 메인 카테고리가 있다면(즉, 서브 카테고리인 경우) 메인 카테고리 열기
                    return {
                        ...state,
                        openCategoryList: [...state.openCategoryList, mainId]
                    };
                }
            }
            
            return {
                ...state,
                openCategoryList: [...state.openCategoryList, categoryId] // 메인 카테고리인 경우
            };
        },
        [READ_CATEGORY_LIST_SUCCESS]: (state, { payload: response }) => ({
            ...state,
            readCategoryListResponse: response.data,
            readCategoryListError: null
        }),
        [READ_CATEGORY_LIST_FAILURE]: (state, { payload: error }) => ({
            ...state,
            readCategoryListResponse: null,
            readCategoryListError: error
        }),
        [READ_DOCS_LIST_SUCCESS]: (state, { payload: response }) => ({
            ...state,
            pagination: response.data.pagination,
            readDocsListResponse: response.data.content,
            readDocsListError: null
        }),
        [READ_DOCS_LIST_FAILURE]: (state, { payload: error }) => ({
            ...state,
            pagination: null,
            readDocsListResponse: null,
            readDocsListError: error
        }),
        [DOWNLOAD_DOCS_LIST_SUCCESS]: (state, { payload: response }) => ({
            ...state,
            downloadResponse: response.data,
            downloadError: null
        }),
        [DOWNLOAD_DOCS_LIST_FAILURE]: (state, { payload: error }) => ({
            ...state,
            downloadResponse: null,
            downloadError: error
        }),
        [CHECK_DUPLICATED_NAME_SUCCESS]: (state, { payload: response }) => ({
            ...state,
            duplicateResponse: response,
            duplicateError: null
        }),
        [CHECK_DUPLICATED_NAME_FAILURE]: (state, { payload: error }) => ({
            ...state,
            duplicateResponse: null,
            duplicateError: error.response.data
        }),
        [UPLOAD_DOCS_SUCCESS]: (state, { payload: response }) => ({
            ...state,
            uploadResponse: response,
            uploadError: null
        }),
        [UPLOAD_DOCS_FAILURE]: (state, { payload: error }) => ({
            ...state,
            uploadResponse: null,
            uploadError: error
        }),
        [CREATE_CATEGORY_SUCCESS]: (state, { payload: response }) => {
            const currentId = state.currentId || 0;

            const { id, name, description } = response.data;

            const newCategory = {
                id: id,
                categoryName: name,
                categoryDescription: description,
                depth: currentId === 0 ? 1 : 2
            };
            
            const addCategoryToList = (categoryList) => {
                if (!categoryList) return [newCategory];
                
                if (currentId === 0) {
                    return [...categoryList, newCategory];
                } else {
                    return categoryList.map((category) => { // currentId가 0이 아닌 경우
                        if (category.id === currentId) { // depth 1
                            const updatedSubCategoryList = category.categoryList || [];
                            return {
                                ...category,
                                categoryList: [...updatedSubCategoryList, newCategory]
                            };
                        }
                        
                        if (category.categoryList && category.categoryList.length > 0) { // depth 2
                            return {
                                ...category,
                                categoryList: addCategoryToList(category.categoryList)
                            };
                        }
                        
                        return category;
                    });
                }
            };
            
            const updatedCategoryList = addCategoryToList(state.readCategoryListResponse);
            
            return {
                ...state,
                readCategoryListResponse: updatedCategoryList,
                createResponse: response.data,
                createError: null
            };
        },
        [CREATE_CATEGORY_FAILURE]: (state, { payload: error }) => ({
            ...state,
            createResponse: null,
            createError: error.response.data
        }),
        [UPDATE_CATEGORY_SUCCESS]: (state, { payload: response }) => {
            const updatedCategory = response.data;

            const updateNestedCategoryList = (categoryList) => {
                if (!categoryList) return null;
                
                return categoryList.map((category) => {
                    if (category.id === updatedCategory.id) { // depth 1
                        return {
                            ...category,
                            categoryName: updatedCategory.name,
                            categoryDescription: updatedCategory.description
                        };
                    }
                    
                    if (category.categoryList && category.categoryList.length > 0) { // depth 2
                        return {
                            ...category,
                            categoryList: updateNestedCategoryList(category.categoryList)
                        };
                    }
                    
                    return category;
                });
            };
            
            const updatedCategoryList = updateNestedCategoryList(state.readCategoryListResponse);

            const updatedDocsList = state.readDocsListResponse 
                ? state.readDocsListResponse.map((docsItem) => 
                    docsItem.id === updatedCategory.id 
                        ? { ...docsItem, name: updatedCategory.name, description: updatedCategory.description }
                        : docsItem
                    )
                : null;

            return {
                ...state,
                readCategoryListResponse: updatedCategoryList,
                readDocsListResponse: updatedDocsList,
                updateCategoryResponse: response.data,
                updateCategoryError: null
            };
        },
        [UPDATE_CATEGORY_FAILURE]: (state, { payload: error }) => ({
            ...state,
            updateCategoryResponse: null,
            updateCategoryError: error.response.data
        }),
        [UPDATE_DOCS_SUCCESS]: (state, { payload: response }) => {
            const updatedDocs = response.data;

            const updatedDocsList = state.readDocsListResponse 
                ? state.readDocsListResponse.map((docsItem) => 
                    docsItem.id === updatedDocs.id 
                        ? { ...docsItem, name: updatedDocs.name }
                        : docsItem
                    )
                : null;
            
            return {
                ...state,
                readDocsListResponse: updatedDocsList,
                updateDocsResponse: response.data,
                updateDocsError: null
            };
        },
        [UPDATE_DOCS_FAILURE]: (state, { payload: error }) => ({
            ...state,
            updateDocsResponse: null,
            updateDocsError: error.response.data
        }),
        [DELETE_DOCS_LIST_SUCCESS]: (state, { payload: response }) => {
            const deletedCategoryList = response.data      // type이 CATEGORY인 id 목록
                .filter(item => item.type === 'CATEGORY')
                .map(item => item.id);
            
            const removeCategoryList = (categoryList) => {
                if (!categoryList) return [];
                
                const filteredList = categoryList.filter(   // depth 1
                    category => !deletedCategoryList.includes(category.id)
                );
                    
                return filteredList.map(category => {       // depth 2
                    if (category.categoryList && category.categoryList.length > 0) {
                        return {
                            ...category,
                            categoryList: removeCategoryList(category.categoryList)
                        };
                    }
                    return category;
                });
            };
            
            const updatedCategoryList = removeCategoryList(state.readCategoryListResponse);

            return {
                ...state,
                readCategoryListResponse: updatedCategoryList,
                deleteResponse: response.data,
                deleteError: null
            };
        },
        [DELETE_DOCS_LIST_FAILURE]: (state, { payload: error }) => ({
            ...state,
            deleteResponse: null,
            deleteError: error
        })
    },
    initialState
);

export default docs;
